import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Euro, PlusCircle, Pencil, Trash2, X, Building2, Home, Calendar, TrendingUp, Calculator, AlertCircle } from 'lucide-react';
import Skeleton from './Skeleton';
import toast from 'react-hot-toast';

interface FixedExpense {
  id: string;
  description: string;
  amount: number;
  due_date: number;
  type: 'private' | 'business';
}

interface ExpenseFormData {
  description: string;
  amount: string;
  due_date: string;
  type: 'private' | 'business';
}

export default function FixedExpenses() {
  const [expenses, setExpenses] = useState<FixedExpense[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState<FixedExpense | null>(null);
  const [formData, setFormData] = useState<ExpenseFormData>({
    description: '',
    amount: '',
    due_date: '1',
    type: 'private'
  });

  useEffect(() => {
    fetchExpenses();
  }, []);

  const fetchExpenses = async () => {
    try {
      const { data, error } = await supabase
        .from('fixed_expenses')
        .select('*')
        .order('type', { ascending: true })
        .order('due_date', { ascending: true });

      if (error) throw error;
      setExpenses(data || []);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast.error('Fout bij het ophalen van vaste lasten');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const amount = parseFloat(formData.amount.replace(',', '.'));
      if (isNaN(amount)) {
        toast.error('Voer een geldig bedrag in');
        return;
      }

      const dueDate = parseInt(formData.due_date);
      if (isNaN(dueDate) || dueDate < 1 || dueDate > 31) {
        toast.error('Voer een geldige dag in (1-31)');
        return;
      }

      if (editingExpense) {
        const { error } = await supabase
          .from('fixed_expenses')
          .update({
            description: formData.description,
            amount: amount,
            due_date: dueDate,
            type: formData.type
          })
          .eq('id', editingExpense.id);

        if (error) throw error;
        toast.success('Vaste last bijgewerkt');
      } else {
        const { error } = await supabase
          .from('fixed_expenses')
          .insert([{
            user_id: user.id,
            description: formData.description,
            amount: amount,
            due_date: dueDate,
            type: formData.type
          }]);

        if (error) throw error;
        toast.success('Vaste last toegevoegd');
      }

      setFormData({
        description: '',
        amount: '',
        due_date: '1',
        type: 'private'
      });
      setShowForm(false);
      setEditingExpense(null);
      fetchExpenses();
    } catch (error) {
      console.error('Error saving expense:', error);
      toast.error('Fout bij het opslaan');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Weet je zeker dat je deze vaste last wilt verwijderen?')) return;

    try {
      const { error } = await supabase
        .from('fixed_expenses')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Vaste last verwijderd');
      fetchExpenses();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Fout bij het verwijderen');
    }
  };

  const calculateTotal = (expenses: FixedExpense[]) => {
    return expenses.reduce((sum, expense) => sum + expense.amount, 0);
  };

  const privateExpenses = expenses.filter(expense => expense.type === 'private');
  const businessExpenses = expenses.filter(expense => expense.type === 'business');
  const totalPrivate = calculateTotal(privateExpenses);
  const totalBusiness = calculateTotal(businessExpenses);
  const grandTotal = totalPrivate + totalBusiness;

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-40" />
        </div>

        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-xl shadow-medium p-6">
              <Skeleton className="h-5 w-32 mb-2" />
              <Skeleton className="h-8 w-24 mb-1" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>

        {/* Content Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-xl shadow-medium p-6">
              <div className="space-y-4">
                <Skeleton className="h-6 w-32" />
                {[...Array(3)].map((_, j) => (
                  <div key={j} className="p-4 border rounded-lg">
                    <Skeleton className="h-4 w-full mb-2" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Vaste Lasten
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Beheer je maandelijkse vaste kosten
          </p>
        </div>
        <button
          onClick={() => {
            setEditingExpense(null);
            setFormData({
              description: '',
              amount: '',
              due_date: '1',
              type: 'private'
            });
            setShowForm(true);
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors duration-200"
        >
          <PlusCircle className="h-5 w-5" />
          <span>Nieuwe Vaste Last</span>
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center space-x-3">
            <Home className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Privé</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">€{totalPrivate.toFixed(2)}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{privateExpenses.length} items</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center space-x-3">
            <Building2 className="h-6 w-6 text-green-600 dark:text-green-400" />
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Zakelijk</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">€{totalBusiness.toFixed(2)}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{businessExpenses.length} items</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center space-x-3">
            <Calculator className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Totaal</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">€{grandTotal.toFixed(2)}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">per maand</p>
            </div>
          </div>
        </div>
      </div>

      {/* Expenses Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Private Expenses */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/30 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                  <Home className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Privé Lasten</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Persoonlijke uitgaven</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-blue-600 dark:text-blue-400">€{totalPrivate.toFixed(2)}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">per maand</p>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            {privateExpenses.length > 0 ? (
              <div className="space-y-3">
                {privateExpenses.map((expense) => (
                  <div 
                    key={expense.id} 
                    className="group flex items-center justify-between p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-200"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                          <Euro className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900 dark:text-gray-100">{expense.description}</h3>
                          <div className="flex items-center space-x-4 mt-1">
                            <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                              <Calendar className="h-3 w-3 mr-1" />
                              {expense.due_date}e van de maand
                            </span>
                            <span className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                              €{expense.amount.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <button
                        onClick={() => {
                          setEditingExpense(expense);
                          setFormData({
                            description: expense.description,
                            amount: expense.amount.toString(),
                            due_date: expense.due_date.toString(),
                            type: expense.type
                          });
                          setShowForm(true);
                        }}
                        className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors duration-200"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(expense.id)}
                        className="p-2 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Home className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-sm">Geen privé vaste lasten</p>
                <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">Voeg je eerste privé uitgave toe</p>
              </div>
            )}
          </div>
        </div>

        {/* Business Expenses */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/30 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                  <Building2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Zakelijke Lasten</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Bedrijfsuitgaven</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-green-600 dark:text-green-400">€{totalBusiness.toFixed(2)}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">per maand</p>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            {businessExpenses.length > 0 ? (
              <div className="space-y-3">
                {businessExpenses.map((expense) => (
                  <div 
                    key={expense.id} 
                    className="group flex items-center justify-between p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:border-green-300 dark:hover:border-green-600 transition-all duration-200"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                          <Euro className="h-4 w-4 text-green-600 dark:text-green-400" />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900 dark:text-gray-100">{expense.description}</h3>
                          <div className="flex items-center space-x-4 mt-1">
                            <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                              <Calendar className="h-3 w-3 mr-1" />
                              {expense.due_date}e van de maand
                            </span>
                            <span className="text-lg font-semibold text-green-600 dark:text-green-400">
                              €{expense.amount.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <button
                        onClick={() => {
                          setEditingExpense(expense);
                          setFormData({
                            description: expense.description,
                            amount: expense.amount.toString(),
                            due_date: expense.due_date.toString(),
                            type: expense.type
                          });
                          setShowForm(true);
                        }}
                        className="p-2 text-green-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors duration-200"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(expense.id)}
                        className="p-2 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Building2 className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-sm">Geen zakelijke vaste lasten</p>
                <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">Voeg je eerste zakelijke uitgave toe</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-[99999]">
          <div 
            className="fixed inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => {
              setShowForm(false);
              setEditingExpense(null);
            }}
            aria-hidden="true"
          />
          
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {editingExpense ? 'Vaste Last Bewerken' : 'Nieuwe Vaste Last'}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {editingExpense ? 'Wijzig de gegevens van je vaste last' : 'Voeg een nieuwe maandelijkse uitgave toe'}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowForm(false);
                      setEditingExpense(null);
                    }}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Type uitgave
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, type: 'private' }))}
                        className={`flex items-center justify-center px-4 py-3 border rounded-lg text-sm font-medium transition-all duration-200 ${
                          formData.type === 'private'
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 shadow-sm'
                            : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <Home className="h-4 w-4 mr-2" />
                        Privé
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, type: 'business' }))}
                        className={`flex items-center justify-center px-4 py-3 border rounded-lg text-sm font-medium transition-all duration-200 ${
                          formData.type === 'business'
                            ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 shadow-sm'
                            : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <Building2 className="h-4 w-4 mr-2" />
                        Zakelijk
                      </button>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="description"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Omschrijving *
                    </label>
                    <input
                      type="text"
                      id="description"
                      required
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Bijv. Huur, Verzekering, Internet..."
                      className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors duration-200"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="amount"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Bedrag per maand *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="amount"
                        required
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        placeholder="0,00"
                        className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 pl-8 pr-4 py-3 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors duration-200"
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Euro className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="due_date"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Afschrijvingsdag *
                    </label>
                    <select
                      id="due_date"
                      required
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors duration-200"
                    >
                      {[...Array(31)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {i + 1}e van de maand
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Op welke dag van de maand wordt dit bedrag afgeschreven?
                    </p>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={() => {
                        setShowForm(false);
                        setEditingExpense(null);
                      }}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                    >
                      Annuleren
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 rounded-lg text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-800 transition-colors duration-200"
                    >
                      {editingExpense ? 'Bijwerken' : 'Toevoegen'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}